import type { ReactNode } from 'react'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { formatLibraryFolderSegment } from '../../lib/dateDisplay'
import type { FolderTreeNode, LibrarySidebarTreeModel } from '../../lib/folderTree'
import {
  folderTreeInitialExpandedKeys,
  folderTreeMergeExpandedKeys,
} from '../../lib/libraryFolderTreeUi'
import { LIBRARY_REFERENCE_UPLOAD_SEGMENT } from '../../lib/libraryPaths'
import { IconChevronDown, IconChevronRight, IconLibraryFolder } from '../nav-icons'

export type LibraryFolderTreeViewProps = {
  model: LibrarySidebarTreeModel
  selectedPath: string
  onSelect: (path: string) => void
  angular: boolean
  /** 为 true 时使用「上传的参考图（本机）→ 图库根目录 → 七牛子树」分区布局 */
  showLibraryRootRow?: boolean
  /** 七牛子树无任何文件夹时在缩进区内展示的占位 */
  emptyChildrenSlot?: ReactNode
  /** 新建文件夹选父级等场景：不展示本地「上传的参考图」及与其相关的分隔区块 */
  omitLocalReferenceUpload?: boolean
  /** 侧栏等固定高度容器：七牛子树引导竖线延伸至剩余空间底部 */
  fillRemainingHeight?: boolean
}

function folderSegmentParentPath(nodePath: string): string {
  const parts = nodePath.split('/').filter(Boolean)
  if (parts.length <= 1) return ''
  return parts.slice(0, -1).join('/')
}

export function folderTreeRowClassName(angular: boolean, active: boolean): string {
  return [
    'group flex w-full min-w-0 items-center gap-1.5 px-2 py-1.5 text-left transition',
    angular ? 'rounded-none' : 'rounded-lg',
    active ? 'bg-accent font-medium text-white' : 'text-ink hover:bg-surface-muted',
  ].join(' ')
}

function FolderTreeEmptyStub({ stretch = false }: { stretch?: boolean }) {
  return (
    <div
      role="status"
      className={[
        'flex flex-col items-center justify-center gap-2 py-6 text-center',
        stretch ? 'min-h-0 flex-1' : 'min-h-[5.5rem]',
      ].join(' ')}
    >
      <IconLibraryFolder className="h-8 w-8 shrink-0 opacity-[0.22]" aria-hidden />
      <p className="text-xs leading-relaxed text-ink-soft">暂无子文件夹</p>
    </div>
  )
}

type FolderTreeGuideColumnProps = {
  fill?: boolean
  children: ReactNode
}

/** 子树左侧引导竖线：fill 时占满父级剩余高度 */
function FolderTreeGuideColumn({ fill = false, children }: FolderTreeGuideColumnProps) {
  return (
    <div
      className={['relative ml-1 min-w-0 pl-2 pt-0.5', fill ? 'flex min-h-0 flex-1 flex-col' : ''].join(' ')}
      role="none"
    >
      <div
        className="pointer-events-none absolute bottom-0 left-0 top-0 w-px bg-[color-mix(in_srgb,var(--line)_60%,transparent)]"
        aria-hidden
      />
      <div className={fill ? 'relative flex min-h-0 flex-1 flex-col' : 'relative'}>{children}</div>
    </div>
  )
}

type RootRowProps = {
  angular: boolean
  active: boolean
  onSelect: () => void
}

function LibraryFolderRootRow({ angular, active, onSelect }: RootRowProps) {
  return (
    <button
      type="button"
      className={folderTreeRowClassName(angular, active)}
      style={{ paddingLeft: 8 }}
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
    >
      <IconLibraryFolder className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
      <span className="min-w-0 flex-1 font-medium">图库根目录</span>
    </button>
  )
}

type NodeRowProps = {
  node: FolderTreeNode
  depth: number
  angular: boolean
  selectedPath: string
  expandedKeys: Set<string>
  onToggleExpand: (path: string) => void
  onSelectPath: (path: string) => void
}

/** 单行：有子节点时点按切换展开，同时将该路径作为当前选中（树形语义） */
const FolderTreeNodeRow = memo(function FolderTreeNodeRow({
  node,
  depth,
  angular,
  selectedPath,
  expandedKeys,
  onToggleExpand,
  onSelectPath,
}: NodeRowProps) {
  const hasChildren = node.children.length > 0
  const isOpen = expandedKeys.has(node.path)
  const active = selectedPath === node.path
  const indentPx = 8 + depth * 12

  const activateRow = () => {
    if (hasChildren) onToggleExpand(node.path)
    onSelectPath(node.path)
  }

  return (
    <li className="min-w-0">
      <button
        type="button"
        role="treeitem"
        aria-expanded={hasChildren ? isOpen : undefined}
        className={`flex min-w-0 items-center gap-0.5 ${folderTreeRowClassName(angular, active)}`}
        style={{ paddingLeft: indentPx }}
        onClick={activateRow}
        aria-current={active ? 'true' : undefined}
      >
        <span
          className={[
            'flex h-full w-5 shrink-0 items-center justify-center',
            hasChildren ? 'text-ink-soft group-hover:text-ink' : '',
          ].join(' ')}
          aria-hidden
        >
          {hasChildren ? (
            isOpen ? (
              <IconChevronDown className="h-3.5 w-3.5" />
            ) : (
              <IconChevronRight className="h-3.5 w-3.5" />
            )
          ) : (
            <span className="inline-block w-3.5" />
          )}
        </span>
        <IconLibraryFolder className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
        <span className="min-w-0 flex-1 truncate">
          {formatLibraryFolderSegment(node.segment, folderSegmentParentPath(node.path))}
        </span>
      </button>
      {hasChildren && isOpen ? (
        <ul role="group" className="ml-1 border-l border-line/60 pb-0.5 pl-1 pt-0.5">
          {node.children.map((child) => (
            <FolderTreeNodeRow
              key={child.path}
              node={child}
              depth={depth + 1}
              angular={angular}
              selectedPath={selectedPath}
              expandedKeys={expandedKeys}
              onToggleExpand={onToggleExpand}
              onSelectPath={onSelectPath}
            />
          ))}
        </ul>
      ) : null}
    </li>
  )
})

/**
 * 本机「上传的参考图」顶行：无子文件夹时布局与 {@link LibraryFolderRootRow} 一致（无前导 Chevron，不额外缩进）。
 */
function ReferenceUploadTopBranch({
  node,
  angular,
  selectedPath,
  expandedKeys,
  onToggleExpand,
  onSelectPath,
}: Omit<NodeRowProps, 'depth' | 'node'> & { node: FolderTreeNode }) {
  const hasChildren = node.children.length > 0
  const isOpen = expandedKeys.has(node.path)
  const active = selectedPath === node.path

  const activateRow = () => {
    if (hasChildren) onToggleExpand(node.path)
    onSelectPath(node.path)
  }

  if (!hasChildren) {
    return (
      <div className="min-w-0">
        <button
          type="button"
          role="treeitem"
          className={folderTreeRowClassName(angular, active)}
          style={{ paddingLeft: 8 }}
          onClick={() => onSelectPath(node.path)}
          aria-current={active ? 'true' : undefined}
        >
          <IconLibraryFolder className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
          <span className="min-w-0 flex-1 truncate font-medium">{node.segment}</span>
        </button>
      </div>
    )
  }

  return (
    <div className="min-w-0">
      <button
        type="button"
        role="treeitem"
        aria-expanded={isOpen}
        className={`flex min-w-0 w-full items-center gap-1.5 ${folderTreeRowClassName(angular, active)}`}
        style={{ paddingLeft: 8 }}
        onClick={activateRow}
        aria-current={active ? 'true' : undefined}
      >
        <span className="flex h-full w-5 shrink-0 items-center justify-center text-ink-soft group-hover:text-ink" aria-hidden>
          {isOpen ? <IconChevronDown className="h-3.5 w-3.5" /> : <IconChevronRight className="h-3.5 w-3.5" />}
        </span>
        <IconLibraryFolder className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
        <span className="min-w-0 flex-1 truncate font-medium">{node.segment}</span>
      </button>
      {isOpen ? (
        <ul role="group" className="mt-0.5 ml-1 border-l border-line/60 pb-0.5 pl-1 pt-0.5">
          {node.children.map((child) => (
            <FolderTreeNodeRow
              key={child.path}
              node={child}
              depth={1}
              angular={angular}
              selectedPath={selectedPath}
              expandedKeys={expandedKeys}
              onToggleExpand={onToggleExpand}
              onSelectPath={onSelectPath}
            />
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function useFolderTreeExpansion(roots: FolderTreeNode[], selectedPath: string) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() =>
    folderTreeInitialExpandedKeys(roots, selectedPath),
  )

  useEffect(() => {
    setExpandedKeys((prev) => folderTreeMergeExpandedKeys(prev, roots, selectedPath))
  }, [roots, selectedPath])

  const toggleExpand = useCallback((path: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }, [])

  return { expandedKeys, toggleExpand }
}

/** PC 侧栏、移动抽屉、弹窗：本地上传参考图 → 虚拟图库根 → 七牛子树 */
export function LibraryFolderTreeView({
  model,
  selectedPath,
  onSelect,
  angular,
  showLibraryRootRow = false,
  emptyChildrenSlot,
  omitLocalReferenceUpload = false,
  fillRemainingHeight = false,
}: LibraryFolderTreeViewProps) {
  const rootsForExpansion = useMemo(() => {
    const base = [...model.cloudRoots]
    if (!omitLocalReferenceUpload) base.push(model.referenceUploadRoot)
    return base
  }, [model.cloudRoots, model.referenceUploadRoot, omitLocalReferenceUpload])
  const { expandedKeys, toggleExpand } = useFolderTreeExpansion(rootsForExpansion, selectedPath)
  const rootLibraryActive = selectedPath === ''

  const refRoot = model.referenceUploadRoot
  const refOk =
    !omitLocalReferenceUpload &&
    refRoot.path === LIBRARY_REFERENCE_UPLOAD_SEGMENT &&
    refRoot.segment === LIBRARY_REFERENCE_UPLOAD_SEGMENT

  const cloudSectionTopPad =
    omitLocalReferenceUpload || !refOk ? '' : 'pt-1.5'

  return (
    <nav
      className={['flex min-h-0 flex-col text-sm', fillRemainingHeight ? 'min-h-full flex-1' : ''].join(' ')}
      aria-label="文件夹"
    >
      {showLibraryRootRow ? (
        <div
          className={[
            'flex min-w-0 flex-col gap-0.5',
            fillRemainingHeight ? 'min-h-0 flex-1' : '',
          ].join(' ')}
        >
          {refOk ? (
            <section
              className="min-w-0 border-b border-line/60 pb-2"
              aria-label="本地上传的参考图"
            >
              <ReferenceUploadTopBranch
                node={refRoot}
                angular={angular}
                selectedPath={selectedPath}
                expandedKeys={expandedKeys}
                onToggleExpand={toggleExpand}
                onSelectPath={onSelect}
              />
            </section>
          ) : null}

          <section
            className={[
              'min-w-0 flex flex-col gap-0.5',
              cloudSectionTopPad,
              fillRemainingHeight ? 'min-h-0 flex-1' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label="七牛图库目录"
          >
            <LibraryFolderRootRow angular={angular} active={rootLibraryActive} onSelect={() => onSelect('')} />
            {model.cloudRoots.length === 0 ? (
              <FolderTreeGuideColumn fill={fillRemainingHeight}>
                {emptyChildrenSlot ?? <FolderTreeEmptyStub stretch={fillRemainingHeight} />}
              </FolderTreeGuideColumn>
            ) : (
              <FolderTreeGuideColumn fill={fillRemainingHeight}>
                <ul role="group" className="mt-0.5 space-y-0.5">
                  {model.cloudRoots.map((node) => (
                    <FolderTreeNodeRow
                      key={node.path}
                      node={node}
                      depth={0}
                      angular={angular}
                      selectedPath={selectedPath}
                      expandedKeys={expandedKeys}
                      onToggleExpand={toggleExpand}
                      onSelectPath={onSelect}
                    />
                  ))}
                </ul>
              </FolderTreeGuideColumn>
            )}
          </section>
        </div>
      ) : (
        <>
          {refOk ? (
            <section className="mb-3 min-w-0 border-b border-line/60 pb-3" aria-label="本地上传的参考图">
              <ReferenceUploadTopBranch
                node={refRoot}
                angular={angular}
                selectedPath={selectedPath}
                expandedKeys={expandedKeys}
                onToggleExpand={toggleExpand}
                onSelectPath={onSelect}
              />
            </section>
          ) : null}
          {model.cloudRoots.length === 0 ? (
            (emptyChildrenSlot ?? <FolderTreeEmptyStub />)
          ) : (
            <ul role="tree" className="space-y-0.5">
              {model.cloudRoots.map((node) => (
                <FolderTreeNodeRow
                  key={node.path}
                  node={node}
                  depth={0}
                  angular={angular}
                  selectedPath={selectedPath}
                  expandedKeys={expandedKeys}
                  onToggleExpand={toggleExpand}
                  onSelectPath={onSelect}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </nav>
  )
}
